import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Send, Bot, User, Sparkles, Loader2, 
  LogIn, UserPlus, Lock, Mail, Phone, ArrowRight,
  Calendar, MessageSquare, CheckCircle2, ChevronRight,
  ShoppingBag, ExternalLink, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { publicAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

export default function PublicAIChatModal({ isOpen, onClose, initialQuery, tenantId, tenantName }) {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [guestMessageCount, setGuestMessageCount] = useState(0);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [showLeadQualification, setShowLeadQualification] = useState(false);
  const [signupData, setSignupData] = useState({ name: '', email: '', phone: '' });
  const [qualificationData, setQualificationData] = useState({ 
    serviceInterest: '', 
    message: '', 
    wantsCall: false 
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [leadRegistered, setLeadRegistered] = useState(false);
  const [services, setServices] = useState([]);
  const [calendarBookingUrl, setCalendarBookingUrl] = useState('');
  const [feedback, setFeedback] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const MAX_GUEST_MESSAGES = 2;

  useEffect(() => {
    if (isOpen) {
      fetchServices();
      if (initialQuery) {
        setMessages([]);
        setGuestMessageCount(0);
        setShowSignupForm(false);
        setShowLeadQualification(false);
        setLeadRegistered(false);
        
        setTimeout(() => {
          sendMessage(initialQuery);
        }, 500);
      }
    }
  }, [isOpen, initialQuery]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !showSignupForm && !showLeadQualification) {
      inputRef.current?.focus();
    }
  }, [isOpen, showSignupForm, showLeadQualification]);

  const fetchServices = async () => {
    try {
      const res = await publicAPI.get(`/portal/${tenantId || 'demo'}`);
      setServices(res.data?.services || []);
      // Get calendar booking URL from tenant settings
      if (res.data?.tenant?.calendar_booking_url) {
        setCalendarBookingUrl(res.data.tenant.calendar_booking_url);
      }
    } catch (e) {
      console.error('Failed to fetch services:', e);
    }
  };

  const sendMessage = async (messageText) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    // Block guests after 2 messages
    if (!isAuthenticated && !leadRegistered && guestMessageCount >= MAX_GUEST_MESSAGES) {
      setShowSignupForm(true);
      return;
    }

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setIsLoading(true);

    try {
      const response = await publicAPI.post('/ai/public/chat', {
        message: text,
        session_id: sessionId,
        tenant_id: tenantId,
        user_id: user?.id || null
      });

      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      // Parse response for action buttons
      const aiResponse = response.data.response;
      const hasServiceMention = /service|hosting|support|plan|price/i.test(aiResponse);
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: aiResponse,
        showActions: hasServiceMention && !leadRegistered
      }]);

      // Track guest messages
      if (!isAuthenticated && !leadRegistered) {
        const newCount = guestMessageCount + 1;
        setGuestMessageCount(newCount);
        
        // Show signup after 2 messages
        if (newCount >= MAX_GUEST_MESSAGES) {
          setTimeout(() => {
            setShowSignupForm(true);
          }, 2000);
        }
      }

    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, { 
        role: 'system', 
        content: err.response?.status === 402 
          ? 'Our AI assistant is temporarily unavailable. Please contact us directly.'
          : 'Something went wrong. Please try again.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    
    if (!signupData.name || !signupData.email) {
      setSignupError('Name and email are required');
      return;
    }

    setSignupLoading(true);
    try {
      await publicAPI.post('/ai/register-lead', {
        full_name: signupData.name,
        email: signupData.email,
        phone: signupData.phone || null,
        tenant_id: tenantId,
        source: 'ai_chat',
        session_id: sessionId
      });

      // Move to lead qualification
      setShowSignupForm(false);
      setShowLeadQualification(true);
      setLeadRegistered(true);
      
    } catch (err) {
      setSignupError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const handleQualificationSubmit = async () => {
    // Save qualification data
    try {
      await publicAPI.post('/ai/lead-qualification', {
        email: signupData.email,
        tenant_id: tenantId,
        service_interest: qualificationData.serviceInterest,
        message: qualificationData.message,
        wants_call: qualificationData.wantsCall,
        session_id: sessionId
      });
    } catch (e) {
      console.error('Failed to save qualification:', e);
    }

    // Continue chat
    setShowLeadQualification(false);
    setMessages(prev => [...prev, { 
      role: 'system', 
      content: `Thanks ${signupData.name}! I've noted your interest${qualificationData.serviceInterest ? ` in ${qualificationData.serviceInterest}` : ''}. How can I help you further?`,
      isSuccess: true
    }]);
  };

  const handleActionClick = (action) => {
    if (action === 'services') {
      window.open(`/app/${tenantId || 'demo'}/services`, '_blank');
    } else if (action === 'contact') {
      if (calendarBookingUrl) {
        window.open(calendarBookingUrl, '_blank');
      } else {
        if (!leadRegistered) {
          setShowSignupForm(true);
        } else {
          setShowLeadQualification(true);
        }
      }
    }
  };

  const submitFeedback = async (msgIndex, rating) => {
    const prev = feedback[msgIndex];
    const newRating = prev === rating ? null : rating;
    setFeedback(f => ({ ...f, [msgIndex]: newRating }));
    if (!newRating) return;
    try {
      await publicAPI.post('/ai/feedback', {
        session_id: sessionId,
        message_index: msgIndex,
        rating: newRating,
        is_public: true,
        tenant_id: tenantId
      });
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(255, 255, 255, 0.85)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl h-[80vh] max-h-[700px] rounded-3xl shadow-2xl overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #f97316, #ec4899)', padding: '3px' }}
        >
          <div className="w-full h-full bg-white rounded-[21px] flex flex-col overflow-hidden relative">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-pink-50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center shadow-lg">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">AI Assistant</h3>
                  <p className="text-sm text-gray-500">{tenantName || 'Here to help'}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-3 rounded-xl hover:bg-white/80 transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/50">
              {messages.map((msg, idx) => (
                <div key={idx}>
                  {msg.isError ? (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center">
                      <p className="text-sm text-red-600">{msg.content}</p>
                    </div>
                  ) : msg.isSuccess ? (
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-green-700">{msg.content}</p>
                    </div>
                  ) : (
                    <div className={cn("flex gap-3", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                      {msg.role === 'assistant' && (
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-md">
                          <Bot className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div className="flex flex-col gap-2 max-w-[80%]">
                        <div className={cn(
                          "rounded-2xl px-5 py-3 text-sm leading-relaxed",
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-br-md shadow-md'
                            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                        )}>
                          {msg.content}
                        </div>
                        
                        {/* Action buttons for AI responses */}
                        {msg.role === 'assistant' && msg.showActions && (
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => handleActionClick('services')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-lg hover:bg-orange-200 transition-colors"
                            >
                              <ShoppingBag className="w-3.5 h-3.5" />
                              View Services
                              <ExternalLink className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleActionClick('contact')}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-pink-100 text-pink-700 text-xs font-medium rounded-lg hover:bg-pink-200 transition-colors"
                            >
                              <Calendar className="w-3.5 h-3.5" />
                              {calendarBookingUrl ? 'Book a Call' : 'Schedule a Call'}
                              {calendarBookingUrl && <ExternalLink className="w-3 h-3" />}
                            </button>
                          </div>
                        )}

                        {/* Feedback thumbs for assistant messages */}
                        {msg.role === 'assistant' && (
                          <div className="flex items-center gap-1 ml-1">
                            <button
                              onClick={() => submitFeedback(idx, 'up')}
                              className={cn(
                                "p-1 rounded transition-colors",
                                feedback[idx] === 'up'
                                  ? "text-green-500"
                                  : "text-gray-300 hover:text-green-400"
                              )}
                              data-testid={`public-feedback-up-${idx}`}
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => submitFeedback(idx, 'down')}
                              className={cn(
                                "p-1 rounded transition-colors",
                                feedback[idx] === 'down'
                                  ? "text-red-400"
                                  : "text-gray-300 hover:text-red-300"
                              )}
                              data-testid={`public-feedback-down-${idx}`}
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-10 h-10 rounded-xl bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-gray-600" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center shadow-md">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-5 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                      <span className="text-sm text-gray-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Signup Form Overlay - NO Continue as Guest */}
            <AnimatePresence>
              {showSignupForm && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute inset-0 bg-white flex items-center justify-center p-6 rounded-[21px]"
                >
                  <div className="w-full max-w-md">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Sparkles className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Continue the Conversation</h3>
                      <p className="text-gray-600 text-sm mt-2">Quick signup to get personalized help</p>
                    </div>

                    <form onSubmit={handleSignup} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                        <input
                          type="text"
                          value={signupData.name}
                          onChange={(e) => setSignupData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Doe"
                          className="w-full h-12 px-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                          required
                          autoFocus
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="email"
                            value={signupData.email}
                            onChange={(e) => setSignupData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="john@example.com"
                            className="w-full h-12 pl-12 pr-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                            required
                          />
                        </div>
                      </div>

                      {signupError && <p className="text-sm text-red-600 text-center">{signupError}</p>}

                      <button
                        type="submit"
                        disabled={signupLoading}
                        className="w-full py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {signupLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            Continue
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </button>
                      
                      <p className="text-xs text-gray-500 text-center">
                        Already have an account? <a href="/login" className="text-orange-500 hover:underline">Log in</a>
                      </p>
                    </form>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Lead Qualification Form */}
            <AnimatePresence>
              {showLeadQualification && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute inset-0 bg-white flex items-center justify-center p-6 rounded-[21px] overflow-y-auto"
                >
                  <div className="w-full max-w-md">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <CheckCircle2 className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Welcome, {signupData.name}!</h3>
                      <p className="text-gray-600 text-sm mt-2">Help us understand your needs better</p>
                    </div>

                    <div className="space-y-4">
                      {/* Service Interest */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">What service interests you?</label>
                        <div className="grid grid-cols-2 gap-2">
                          {services.slice(0, 4).map((service) => (
                            <button
                              key={service.id}
                              type="button"
                              onClick={() => setQualificationData(prev => ({ ...prev, serviceInterest: service.name }))}
                              className={cn(
                                "p-3 rounded-xl border-2 text-left transition-all text-sm",
                                qualificationData.serviceInterest === service.name
                                  ? "border-orange-500 bg-orange-50"
                                  : "border-gray-200 hover:border-orange-300"
                              )}
                            >
                              <p className="font-medium text-gray-900 truncate">{service.name}</p>
                              <p className="text-xs text-gray-500">${service.price || service.pricing_tiers?.[0]?.price}/mo</p>
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setQualificationData(prev => ({ ...prev, serviceInterest: 'Not sure yet' }))}
                          className={cn(
                            "w-full mt-2 p-2 rounded-lg border text-sm",
                            qualificationData.serviceInterest === 'Not sure yet'
                              ? "border-orange-500 bg-orange-50 text-orange-700"
                              : "border-gray-200 text-gray-600 hover:border-orange-300"
                          )}
                        >
                          Not sure yet - need guidance
                        </button>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tell us about your needs (optional)</label>
                        <textarea
                          value={qualificationData.message}
                          onChange={(e) => setQualificationData(prev => ({ ...prev, message: e.target.value }))}
                          placeholder="What are you looking to achieve?"
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none"
                        />
                      </div>

                      {/* Schedule Call Option */}
                      <div className="bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl p-4">
                        {calendarBookingUrl ? (
                          <a 
                            href={calendarBookingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between group"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">Book a Call</p>
                                <p className="text-xs text-gray-600">Schedule a time that works for you</p>
                              </div>
                            </div>
                            <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                          </a>
                        ) : (
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={qualificationData.wantsCall}
                              onChange={(e) => setQualificationData(prev => ({ ...prev, wantsCall: e.target.checked }))}
                              className="w-5 h-5 mt-0.5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                            />
                            <div>
                              <p className="font-medium text-gray-900 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-orange-500" />
                                I'd like to schedule a call
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                We'll reach out to arrange a convenient time
                              </p>
                            </div>
                          </label>
                        )}
                      </div>

                      <button
                        onClick={handleQualificationSubmit}
                        className="w-full py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                      >
                        Continue to Chat
                        <ArrowRight className="w-5 h-5" />
                      </button>

                      <button
                        onClick={() => {
                          setShowLeadQualification(false);
                          setMessages(prev => [...prev, { 
                            role: 'system', 
                            content: `Welcome ${signupData.name}! How can I help you today?`,
                            isSuccess: true
                          }]);
                        }}
                        className="w-full text-sm text-gray-500 hover:text-gray-700"
                      >
                        Skip for now
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input Area */}
            {!showSignupForm && !showLeadQualification && (
              <div className="p-4 border-t border-gray-100 bg-white">
                {/* Guest message counter */}
                {!isAuthenticated && !leadRegistered && guestMessageCount > 0 && (
                  <div className="mb-3 text-center">
                    <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {MAX_GUEST_MESSAGES - guestMessageCount} free message{MAX_GUEST_MESSAGES - guestMessageCount !== 1 ? 's' : ''} remaining
                    </span>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask me anything about our services..."
                    disabled={isLoading}
                    className="flex-1 h-12 px-5 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all disabled:opacity-50"
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || isLoading}
                    className="h-12 w-12 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white flex items-center justify-center hover:shadow-lg transition-all disabled:opacity-50 disabled:hover:shadow-none"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
